import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Music, Shield, CheckCircle2, CreditCard, Mail } from "lucide-react";
import { useLandingLanguage } from "@/hooks/useLandingLanguage";
import { toast } from "sonner";

export const PublicFooter = () => {
  const { t } = useLandingLanguage();

  return (
    <footer className="border-t bg-muted/20 py-12 sm:py-16">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 gap-8 mb-12 min-[420px]:grid-cols-2 md:grid-cols-5">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <Music className="h-6 w-6 text-primary" />
              <span className="font-bold text-lg">Maestro CRM</span>
            </Link>
            <p className="text-sm text-muted-foreground mb-4">{t.footer.tagline}</p>
            {/* Social Links */}
            <div className="flex gap-3">
              <a href="https://twitter.com/maestrocrm" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="https://linkedin.com/company/innobrain" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://youtube.com/@maestrocrm" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="font-semibold mb-4">{t.footer.product}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/#features" className="hover:text-foreground transition-colors">{t.footer.features}</Link></li>
              <li><Link to="/pricing" className="hover:text-foreground transition-colors">{t.footer.pricing}</Link></li>
              <li><Link to="/#integrations" className="hover:text-foreground transition-colors">{t.footer.integrations}</Link></li>
              <li><Link to="/roadmap" className="hover:text-foreground transition-colors">{t.footer.roadmap}</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold mb-4">{t.footer.company}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/about" className="hover:text-foreground transition-colors">{t.footer.about}</Link></li>
              <li><Link to="/careers" className="hover:text-foreground transition-colors">{t.footer.careers}</Link></li>
              <li><Link to="/contact" className="hover:text-foreground transition-colors">{t.footer.contact}</Link></li>
              <li><Link to="/press" className="hover:text-foreground transition-colors">{t.footer.press}</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-semibold mb-4">{t.footer.resources}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/blog" className="hover:text-foreground transition-colors">{t.footer.blog}</Link></li>
              <li><Link to="/tutorials" className="hover:text-foreground transition-colors">{t.footer.helpCenter}</Link></li>
              <li><Link to="/docs" className="hover:text-foreground transition-colors">{t.footer.docs}</Link></li>
              <li><a href="https://status.maestrocrm.it" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">{t.footer.status}</a></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold mb-4">{t.footer.legal}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/privacy" className="hover:text-foreground transition-colors">{t.footer.privacy}</Link></li>
              <li><Link to="/terms" className="hover:text-foreground transition-colors">{t.footer.terms}</Link></li>
              <li><Link to="/gdpr" className="hover:text-foreground transition-colors">{t.footer.gdpr}</Link></li>
              <li><Link to="/cookie" className="hover:text-foreground transition-colors">{t.footer.cookie}</Link></li>
            </ul>
          </div>
        </div>

        {/* Company Info */}
        <div className="border-t border-b py-8 mb-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="min-w-0 space-y-2 text-sm text-muted-foreground break-words">
              <p className="font-semibold text-foreground">{t.footer.companyInfo.name}</p>
              <p>{t.footer.companyInfo.vat}</p>
              <p>{t.footer.companyInfo.address}</p>
              <p>Email: <a href={`mailto:${t.footer.companyInfo.email}`} className="hover:text-foreground transition-colors">{t.footer.companyInfo.email}</a></p>
              <p>PEC: <a href={`mailto:${t.footer.companyInfo.pec}`} className="hover:text-foreground transition-colors">{t.footer.companyInfo.pec}</a></p>
            </div>
            <div className="flex flex-col items-stretch gap-3 min-[420px]:flex-row min-[420px]:flex-wrap md:justify-end">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10 text-green-600 text-sm font-medium">
                <Shield className="h-4 w-4" />
                {t.footer.trust.gdpr}
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 text-blue-600 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4" />
                {t.footer.trust.ssl}
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 text-amber-600 text-sm font-medium">
                <CreditCard className="h-4 w-4" />
                {t.footer.trust.stripe}
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter */}
        <div className="border-b pb-8 mb-8">
          <div className="max-w-md mx-auto text-center">
            <p className="text-sm text-muted-foreground mb-4">{t.footer.newsletter.title}</p>
            <form className="flex flex-col gap-2 min-[420px]:flex-row" onSubmit={(e) => { e.preventDefault(); toast.success(t.footer.newsletter.success); }}>
              <Input
                type="email"
                placeholder={t.footer.newsletter.placeholder}
                className="flex-1"
                required
              />
              <Button type="submit" size="sm" className="min-h-11 shrink-0">
                <Mail className="h-4 w-4 mr-2" />
                {t.footer.newsletter.button}
              </Button>
            </form>
          </div>
        </div>

        {/* Copyright */}
        <div className="flex flex-col items-start justify-between gap-4 text-sm text-muted-foreground md:flex-row md:items-center">
          <p className="break-words">© {new Date().getFullYear()} MaestroCRM by InnoBrain JC Srl. {t.footer.copyright}</p>
          <p>{t.footer.madeWith}</p>
        </div>
      </div>
    </footer>
  );
};
